import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useMarkerStore } from '../../../store/markerStore';
import useLegendStore from '../../../store/legendStore';
import useMapStore from '../../../store/mapStore';
import { useAnalyticsSelectionStore } from '../../../store/analyticsSelectionStore';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const LoadingIcon = <LoadingOutlined style={{ fontSize: 48 }} spin />;

type RehydrationLoaderProps = PropsWithChildren;

const RehydrationLoader = ({ children }: RehydrationLoaderProps) => {
  const [hydrationState, setHydrationState] = useState({
    hasMarkerStoreHydrated: useMarkerStore.persist.hasHydrated(),
    hasLegendStoreHydrated: useLegendStore.persist.hasHydrated(),
    hasMapStoreHydrated: useMapStore.persist.hasHydrated(),
    hasAnalyticsSelectionStoreHydrated:
      useAnalyticsSelectionStore.persist.hasHydrated(),
  });

  useEffect(() => {
    const unsubOnHydrateMarkerStore = useMarkerStore.persist.onHydrate(() => {
      setHydrationState((prevState) => ({
        ...prevState,
        hasMarkerStoreHydrated: false,
      }));
    });

    const unsubOnFinishHydrateMarkerStore =
      useMarkerStore.persist.onFinishHydration(() => {
        setHydrationState((prevState) => ({
          ...prevState,
          hasMarkerStoreHydrated: true,
        }));
      });

    const unsubOnHydrateLegendStore = useLegendStore.persist.onHydrate(() => {
      setHydrationState((prevState) => ({
        ...prevState,
        hasLegendStoreHydrated: false,
      }));
    });

    const unsubOnFinishHydrateLegendStore =
      useLegendStore.persist.onFinishHydration(() => {
        setHydrationState((prevState) => ({
          ...prevState,
          hasLegendStoreHydrated: true,
        }));
      });

    const unsubOnHydrateMapStore = useMapStore.persist.onHydrate(() => {
      setHydrationState((prevState) => ({
        ...prevState,
        hasMapStoreHydrated: false,
      }));
    });

    const unsubOnFinishHydrateMapStore = useMapStore.persist.onFinishHydration(
      () => {
        setHydrationState((prevState) => ({
          ...prevState,
          hasMapStoreHydrated: true,
        }));
      }
    );

    const unsubOnHydrateAnalyticsSelectionStore =
      useAnalyticsSelectionStore.persist.onHydrate(() => {
        setHydrationState((prevState) => ({
          ...prevState,
          hasAnalyticsSelectionStoreHydrated: false,
        }));
      });

    const unsubOnFinishHydrateAnalyticsSelectionStore =
      useAnalyticsSelectionStore.persist.onFinishHydration(() => {
        setHydrationState((prevState) => ({
          ...prevState,
          hasAnalyticsSelectionStoreHydrated: true,
        }));
      });

    return () => {
      unsubOnHydrateMarkerStore();
      unsubOnFinishHydrateMarkerStore();
      unsubOnHydrateLegendStore();
      unsubOnFinishHydrateLegendStore();
      unsubOnHydrateMapStore();
      unsubOnFinishHydrateMapStore();
      unsubOnHydrateAnalyticsSelectionStore();
      unsubOnFinishHydrateAnalyticsSelectionStore();
    };
  }, []);

  const hasAllHydrated = Object.keys(hydrationState).every((key) =>
    Boolean(hydrationState[key as keyof typeof hydrationState])
  );

  if (!hasAllHydrated) {
    return (
      <Spin
        size='large'
        style={{
          width: '100%',
          height: '20vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        indicator={LoadingIcon}
        spinning
      />
    );
  }

  return <>{children}</>;
};

export default RehydrationLoader;
